import clsx from 'clsx';
import { getScrollBehavior } from 'core/utils/motion';
import React, { useEffect, useRef, useState } from 'react';

type BasicListItemElement = React.ReactElement<{ liActive?: string }>;

type ItemProps = Readonly<{
  guidUrl?: string;
  item: BasicListItemElement;
}>;

const DEEP_LINK_HIGHLIGHT_DELAY_MS = 1000;
const DEEP_LINK_HIGHLIGHT_RESET_DELAY_MS = 3000;

function Item({ guidUrl, item }: ItemProps): JSX.Element {
  const [isActive, setIsActive] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);
  const isDeepLinkTarget = Boolean(guidUrl) && guidUrl === item?.props?.liActive;

  useEffect(() => {
    if (!isDeepLinkTarget) return undefined;

    const activateTimer = setTimeout(() => {
      setIsActive(true);
      const element = itemRef.current;
      if (element && document.activeElement === document.body) {
        // Imperativo y no en JSX: useStream vacía guidUrl a la vez y React quitaría el tabindex antes del foco.
        element.setAttribute('tabindex', '-1');
        element.addEventListener('blur', () => element.removeAttribute('tabindex'), { once: true });
        element.focus();
      }
    }, DEEP_LINK_HIGHLIGHT_DELAY_MS);
    const deactivateTimer = setTimeout(() => setIsActive(false), DEEP_LINK_HIGHLIGHT_RESET_DELAY_MS);

    return () => {
      clearTimeout(activateTimer);
      clearTimeout(deactivateTimer);
    };
  }, [isDeepLinkTarget]);

  return (
    <li aria-current={isActive ? 'location' : undefined} className={clsx('basic-list__item', isActive && 'basic-list__item--active')} ref={itemRef}>
      {item}
    </li>
  );
}

export type BasicListProps = Readonly<{
  as?: 'ol' | 'ul';
  guidUrl?: string;
  items?: BasicListItemElement[];
  withBorder?: boolean;
}>;

function BasicList({ as: Component = 'ul', guidUrl, items = [], withBorder }: BasicListProps): JSX.Element {
  useEffect(() => {
    if (!guidUrl) return undefined;

    const element = document.getElementById(guidUrl);
    const scrollTimer = setTimeout(() => {
      element?.scrollIntoView({ behavior: getScrollBehavior(), block: 'center' });
    }, DEEP_LINK_HIGHLIGHT_DELAY_MS);

    return () => clearTimeout(scrollTimer);
  }, [guidUrl]);

  return (
    <Component className={clsx('basic-list', withBorder && 'basic-list--with-border')} role="list">
      {items.map((item, i) => (
        <Item key={item?.key ?? i} guidUrl={guidUrl} item={item} />
      ))}
    </Component>
  );
}

export default BasicList;
